import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AccountBalanceWallet as WalletIcon,
  CheckCircle as CheckCircleIcon,
  HourglassEmpty as PendingIcon,
  Cancel as CancelIcon,
  Sync as ProcessingIcon,
  PlayArrow as ExecuteIcon,
  Refresh as RefreshIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { environment } from '../config/environment';
import { getApiHeaders, getApiHeadersForGet } from '../utils/apiUtils';

type WithdrawalStatus = 'pending' | 'approved' | 'processing' | 'completed' | 'rejected';

interface WithdrawalRequest {
  id: string;
  _id?: string;
  amount: number;
  withdrawal_type: string;
  provider: string | null;
  destination_email: string | null;
  status: WithdrawalStatus;
  admin_notes: string | null;
  user_email: string | null;
  user_name: string | null;
  createdAt: string;
  completed_at?: string | null;
  wallet_transaction_id?: string | null;
}

const adminSelectableStatuses: WithdrawalStatus[] = ['pending', 'approved', 'processing', 'rejected'];

const isWalletDebited = (request: WithdrawalRequest) => Boolean(request.wallet_transaction_id);

const canCompleteWithdrawal = (request: WithdrawalRequest) => {
  if (request.status === 'rejected') return false;
  if (isWalletDebited(request)) return false;
  return ['pending', 'approved', 'processing', 'completed'].includes(request.status);
};

interface WithdrawalsResponse {
  success: boolean;
  total: number;
  data: WithdrawalRequest[];
}

interface EditState {
  admin_notes: string;
  status: WithdrawalStatus;
}

const statusConfig: Record<
  WithdrawalStatus,
  { label: string; color: 'default' | 'warning' | 'success' | 'error' | 'info'; icon: React.ReactNode }
> = {
  pending: { label: 'Pending', color: 'warning', icon: <PendingIcon fontSize="small" /> },
  approved: { label: 'Approved', color: 'info', icon: <CheckCircleIcon fontSize="small" /> },
  processing: { label: 'Processing', color: 'default', icon: <ProcessingIcon fontSize="small" /> },
  completed: { label: 'Completed', color: 'success', icon: <CheckCircleIcon fontSize="small" /> },
  rejected: { label: 'Rejected', color: 'error', icon: <CancelIcon fontSize="small" /> },
};

const providerLabels: Record<string, string> = {
  tango: 'Tango Card',
  visa: 'Visa',
  mastercard: 'Mastercard',
  paypal: 'PayPal',
};

const WalletWithdrawalsPanel: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [requests, setRequests] = useState<WithdrawalRequest[]>([]);
  const [filterStatus, setFilterStatus] = useState<'all' | WithdrawalStatus>('all');
  const [editState, setEditState] = useState<Record<string, EditState>>({});
  const [saving, setSaving] = useState<Record<string, boolean>>({});
  const [completing, setCompleting] = useState<Record<string, boolean>>({});
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const loadWithdrawals = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const qs = filterStatus === 'all' ? '' : `?status=${filterStatus}`;
      const response = await fetch(`${environment.apiBaseUrl}/payouts/wallet-withdrawals${qs}`, {
        headers: getApiHeadersForGet() as HeadersInit,
      });
      if (!response.ok) throw new Error(`Error ${response.status}: ${response.statusText}`);
      const data: WithdrawalsResponse = await response.json();
      setRequests(data.data || []);
    } catch (err: any) {
      setError(err.message || 'Error loading wallet withdrawals');
    } finally {
      setLoading(false);
    }
  }, [filterStatus]);

  useEffect(() => {
    loadWithdrawals();
  }, [loadWithdrawals]);

  useEffect(() => {
    const next: Record<string, EditState> = {};
    requests.forEach(req => {
      const id = req.id || String(req._id);
      const savedStatus = req.status || 'pending';
      next[id] = {
        admin_notes: req.admin_notes || '',
        status: adminSelectableStatuses.includes(savedStatus) ? savedStatus : 'approved',
      };
    });
    setEditState(next);
  }, [requests]);

  const getAdminUserId = () => {
    try {
      const raw = localStorage.getItem('petflix_user_info');
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return parsed?._id || parsed?.id || null;
    } catch {
      return null;
    }
  };

  const handleSaveStatus = async (requestId: string) => {
    const form = editState[requestId];
    if (!form) return;
    setSaving(prev => ({ ...prev, [requestId]: true }));
    try {
      const response = await fetch(`${environment.apiBaseUrl}/payouts/wallet-withdrawals/${requestId}/status`, {
        method: 'PATCH',
        headers: getApiHeaders() as HeadersInit,
        body: JSON.stringify({
          status: form.status,
          admin_notes: form.admin_notes,
          adminUserId: getAdminUserId(),
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data?.message || data?.error || `Error ${response.status}`);
      setSnackbar({ open: true, message: 'Withdrawal status updated', severity: 'success' });
      await loadWithdrawals();
    } catch (err: any) {
      setSnackbar({ open: true, message: err.message || 'Error updating status', severity: 'error' });
    } finally {
      setSaving(prev => ({ ...prev, [requestId]: false }));
    }
  };

  const handleComplete = async (request: WithdrawalRequest) => {
    const requestId = request.id || String(request._id);
    const confirmed = window.confirm(
      `Confirm you already sent $${Number(request.amount).toFixed(2)} ` +
        `(${providerLabels[request.provider || ''] || request.provider || 'gift card'}) ` +
        `to ${request.destination_email || 'the user'}.\n\nThis will debit their wallet balance.`
    );
    if (!confirmed) return;

    setCompleting(prev => ({ ...prev, [requestId]: true }));
    try {
      const response = await fetch(`${environment.apiBaseUrl}/payouts/wallet-withdrawals/${requestId}/complete`, {
        method: 'POST',
        headers: getApiHeaders() as HeadersInit,
        body: JSON.stringify({ adminUserId: getAdminUserId() }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data?.message || data?.error || `Error ${response.status}`);
      setSnackbar({ open: true, message: 'Withdrawal completed and wallet debited', severity: 'success' });
      await loadWithdrawals();
    } catch (err: any) {
      setSnackbar({ open: true, message: err.message || 'Error completing withdrawal', severity: 'error' });
    } finally {
      setCompleting(prev => ({ ...prev, [requestId]: false }));
    }
  };

  const filtered = requests.filter(req =>
    filterStatus === 'all' ? true : req.status === filterStatus
  );

  const totalPending = requests.filter(r => r.status === 'pending').length;
  const totalCompleted = requests.filter(r => r.status === 'completed').length;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="body2" color="text.secondary">
          {requests.length} requests · <strong>{totalPending}</strong> pending · <strong>{totalCompleted}</strong> completed
        </Typography>
        <Tooltip title="Refresh">
          <Button size="small" startIcon={<RefreshIcon />} onClick={loadWithdrawals}>
            Refresh
          </Button>
        </Tooltip>
      </Box>

      <Box sx={{ mb: 3 }}>
        <ToggleButtonGroup
          value={filterStatus}
          exclusive
          onChange={(_, v) => { if (v !== null) setFilterStatus(v); }}
          size="small"
        >
          <ToggleButton value="all">All</ToggleButton>
          <ToggleButton value="pending">Pending</ToggleButton>
          <ToggleButton value="approved">Approved</ToggleButton>
          <ToggleButton value="processing">Processing</ToggleButton>
          <ToggleButton value="completed">Completed</ToggleButton>
          <ToggleButton value="rejected">Rejected</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {filtered.length === 0 && (
        <Alert severity="info">
          {filterStatus === 'all'
            ? 'No wallet withdrawal requests yet.'
            : `No requests with status "${filterStatus}".`}
        </Alert>
      )}

      <Grid container spacing={3}>
        {filtered.map(request => {
          const requestId = request.id || String(request._id);
          const form = editState[requestId] ?? {
            admin_notes: request.admin_notes || '',
            status: request.status,
          };
          const cfg = statusConfig[request.status] ?? statusConfig.pending;
          const canComplete = canCompleteWithdrawal(request);
          const walletDebited = isWalletDebited(request);
          const completedWithoutDebit = request.status === 'completed' && !walletDebited;

          return (
            <Grid size={{ xs: 12, md: 6 }} key={requestId}>
              <Paper sx={{ p: 2.5, height: '100%', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                  <Chip
                    icon={<WalletIcon fontSize="small" />}
                    label={`$${Number(request.amount).toFixed(2)}`}
                    color="primary"
                    size="small"
                    sx={{ fontWeight: 'bold' }}
                  />
                  <Chip
                    icon={cfg.icon as any}
                    label={cfg.label}
                    color={cfg.color}
                    size="small"
                    variant="outlined"
                  />
                </Box>

                <Typography variant="subtitle1" fontWeight="bold">
                  {request.user_name || 'User'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {request.user_email || '—'}
                </Typography>
                <Divider />

                <Box>
                  <Typography variant="caption" color="text.secondary">Delivery</Typography>
                  <Typography variant="body2">
                    {providerLabels[request.provider || ''] || request.provider || '—'} → {request.destination_email || '—'}
                  </Typography>
                </Box>

                <Typography variant="caption" color="text.secondary">
                  Requested {request.createdAt ? new Date(request.createdAt).toLocaleString() : '—'}
                </Typography>

                {completedWithoutDebit && (
                  <Alert severity="warning" sx={{ py: 0.5 }}>
                    Marked completed but wallet was not debited. Use the button below to debit.
                  </Alert>
                )}

                {walletDebited && (
                  <Typography variant="caption" color="success.main">
                    Wallet debited
                  </Typography>
                )}

                <FormControl size="small" fullWidth disabled={walletDebited}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    label="Status"
                    value={form.status}
                    onChange={e =>
                      setEditState(prev => ({
                        ...prev,
                        [requestId]: { ...form, status: e.target.value as WithdrawalStatus },
                      }))
                    }
                  >
                    {adminSelectableStatuses.map(key => (
                      <MenuItem key={key} value={key}>{statusConfig[key].label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  size="small"
                  label="Admin notes"
                  multiline
                  minRows={2}
                  value={form.admin_notes}
                  onChange={e =>
                    setEditState(prev => ({
                      ...prev,
                      [requestId]: { ...form, admin_notes: e.target.value },
                    }))
                  }
                />

                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 'auto' }}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<SaveIcon />}
                    disabled={!!saving[requestId] || walletDebited}
                    onClick={() => handleSaveStatus(requestId)}
                  >
                    Save status
                  </Button>
                  <Tooltip
                    title={
                      canComplete
                        ? 'Send the gift card externally, then debit the user wallet'
                        : walletDebited
                          ? 'Wallet already debited for this request'
                          : 'Rejected withdrawals cannot be completed'
                    }
                  >
                    <span>
                      <Button
                        size="small"
                        variant="contained"
                        color="success"
                        startIcon={<ExecuteIcon />}
                        disabled={!canComplete || !!completing[requestId]}
                        onClick={() => handleComplete(request)}
                      >
                        Complete & debit wallet
                      </Button>
                    </span>
                  </Tooltip>
                </Box>
              </Paper>
            </Grid>
          );
        })}
      </Grid>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default WalletWithdrawalsPanel;
